/**
 * Resolves the user's preferred rendering of a wardrobe item's photo,
 * honoring the prefer_clean_bg / photo_url_no_bg / clean_bg_color trio.
 *
 * Use this helper everywhere a wardrobe item's photo is displayed
 * (grids, moodboards, orbit boards, item pickers, etc.) so the
 * three-column preference is respected consistently. The only places
 * that should NOT use it are the photo-upload widgets in the edit
 * dialogs (where the raw source image is intentionally shown).
 */
import type { CSSProperties } from "react";

const SWATCHES: Record<string, string> = {
  lilac: "var(--caileve-soft)",
  ivory: "#F5EEE1",
  white: "#FFFFFF",
};

export function bgColorFor(color: string | null | undefined): string {
  const c = color ?? "lilac";
  return SWATCHES[c] ?? SWATCHES.lilac;
}

export type BgPreferenceFields = {
  photo_url: string | null;
  photo_url_no_bg?: string | null;
  prefer_clean_bg?: boolean | null;
  clean_bg_color?: string | null;
};

export type ResolvedPhoto = {
  src: string | null;
  backdropStyle?: CSSProperties;
};

export function resolvePreferredPhoto(item: BgPreferenceFields): ResolvedPhoto {
  const useClean = !!item.prefer_clean_bg && !!item.photo_url_no_bg;
  if (useClean) {
    return {
      src: item.photo_url_no_bg ?? null,
      backdropStyle: { backgroundColor: bgColorFor(item.clean_bg_color) },
    };
  }
  return { src: item.photo_url };
}

/**
 * SELECT fragment for the three bg-preference columns. Append this to
 * any wardrobe-item .select("id, name, photo_url, ...") so the returned
 * rows carry the fields needed by resolvePreferredPhoto.
 */
export const BG_PREFERENCE_SELECT =
  "photo_url_no_bg, prefer_clean_bg, clean_bg_color";

/**
 * Extracts the three bg-preference fields from any item-shaped object,
 * defaulting to null when absent. Spread into a PieceRef / OutfitCandidate
 * / MoodBoardItem literal so the compiler-enforced fields carry through.
 *
 *   candidates.push({ ...bgFields(row), id, type, name, photo_url, priority });
 */
export function bgFields(item: unknown): {
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
} {
  const it = (item ?? {}) as {
    photo_url_no_bg?: string | null;
    prefer_clean_bg?: boolean | null;
    clean_bg_color?: string | null;
  };
  return {
    photo_url_no_bg: it.photo_url_no_bg ?? null,
    prefer_clean_bg: it.prefer_clean_bg ?? null,
    clean_bg_color: it.clean_bg_color ?? null,
  };
}